import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cn } from "@/lib/utils";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";
import type { components } from "@dolfin/api-admin";

type OrganisationSearchHit = components["schemas"]["OrganisationSearchHit"];

interface LinkOrganisationDialogProps {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked: () => void;
}

const BROWSE_LIMIT = 50;
const SEARCH_LIMIT = 20;

/** Adds an existing organisation to a company. Server-side search over all the client's organisations; ones
 * already in a company are shown but disabled (the API would conflict). */
export function LinkOrganisationDialog({
  companyId,
  open,
  onOpenChange,
  onLinked,
}: LinkOrganisationDialogProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<OrganisationSearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<OrganisationSearchHit | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const debouncedQuery = useDebouncedValue(query, 300);
  const searchTerm = debouncedQuery.trim();

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelected(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);

    // No query: browse the first page. With one: server-side search across every organisation.
    const request = searchTerm
      ? api
          .GET("/v1/organisations/search", {
            params: { query: { q: searchTerm, limit: SEARCH_LIMIT } },
          })
          .then(({ data }) => data ?? [])
      : api
          .GET("/v1/organisations", { params: { query: { Limit: BROWSE_LIMIT } } })
          .then(({ data }) =>
            // Reshaped into a search hit for one render path. `score` is a placeholder on browse; don't display it.
            (data?.data ?? []).map<OrganisationSearchHit>((o) => ({
              id: o.id,
              name: o.name,
              contactEmail: o.contactEmail,
              companyId: o.companyId,
              score: 1,
            })),
          );

    request
      .then((hits) => {
        if (!cancelled) setResults(hits);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, searchTerm]);

  async function handleLink() {
    if (!selected?.id) return;
    setSubmitting(true);
    try {
      const { error, response } = await api.PUT("/v1/organisations/{organisationId}/company", {
        params: { path: { organisationId: selected.id } },
        body: { companyId },
      });
      if (error) {
        await toastApiError({ error, response }, "Couldn't add the organisation.");
        return;
      }
      toastApiSuccess("Organisation added to company");
      onLinked();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add an organisation</DialogTitle>
          <DialogDescription>
            Bills received against this company can be routed to any organisation in it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-2">
          <Label htmlFor="link-organisation">Organisation</Label>
          {/* modal: inside a Dialog, whose scroll lock (react-remove-scroll) swallows wheel events on this portaled list. */}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal>
            <PopoverTrigger asChild>
              <Button
                id="link-organisation"
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="w-full justify-between font-normal"
              >
                {selected ? (
                  <span className="truncate">{selected.name}</span>
                ) : (
                  <span className="text-muted-foreground">Search organisations...</span>
                )}
                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
              {/* shouldFilter=false: the server does the matching. */}
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search by name, ID, email, tax no..."
                  value={query}
                  onValueChange={setQuery}
                />
                <CommandList>
                  {loading ? (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                      <Loader2 className="size-3.5 animate-spin" /> Searching...
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>No organisation found.</CommandEmpty>
                      <CommandGroup>
                        {(results ?? []).map((hit) => {
                          const inThisCompany = hit.companyId === companyId;
                          const taken = Boolean(hit.companyId);
                          return (
                            <CommandItem
                              key={hit.id}
                              value={hit.id ?? hit.name}
                              disabled={taken}
                              onSelect={() => {
                                if (taken) return;
                                setSelected(hit);
                                setPickerOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 size-4 shrink-0",
                                  selected?.id === hit.id ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <div className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate">{hit.name}</span>
                                {hit.contactEmail && (
                                  <span className="truncate text-xs text-muted-foreground">
                                    {hit.contactEmail}
                                  </span>
                                )}
                              </div>
                              {taken && (
                                <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                                  {inThisCompany ? "already added" : "in another company"}
                                </span>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-xs text-muted-foreground">
            An organisation already in another company has to be removed from it first.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleLink} disabled={submitting || !selected}>
            {submitting && <Spinner className="size-3.5" />} Add to company
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
